import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ColumnDef,
  DataTable,
} from "../../../../../components/DataTable/DataTable";
import { useToast } from "../../../../../context/ToastContext";
import {
  getUserImportJobDetail,
  getUserImportJobReview,
} from "../../../../../services/api/apiClient";
import {
  UserImportJobRecordActionType,
  UserImportJobReviewSummary,
  UserImportJobReviewUserRow,
} from "../../../../../services/api/types/users/userActions";
import {
  brand,
  error as errorColors,
  gray,
  success as successColors,
  warning as warningColors,
} from "../../../../../theme/themePrimitives";
import { extractErrorMessage } from "../../../../../utils/errorUtils";
import { SectionType, UseBulkUserImportReturn } from "../../useBulkUserImport";
import { PreviewAttribute, UserPreviewModal } from "./UserPreviewModal";

type ReviewChangesProps = Pick<
  UseBulkUserImportReturn,
  | "jobId"
  | "includedSections"
  | "toggleSection"
  | "execute"
  | "isExecuting"
  | "goToStep"
>;

interface SectionConfig {
  type: SectionType;
  label: string;
  dotColor: string;
  textColor: string;
  borderColor: string;
  countTextColor: string;
  countBgColor: string;
  countBorderColor: string;
}

const SECTIONS: SectionConfig[] = [
  {
    type: "new",
    label: "New (Add)",
    dotColor: successColors[500],
    textColor: successColors[600],
    borderColor: successColors[500],
    countTextColor: successColors[700],
    countBgColor: successColors[50],
    countBorderColor: successColors[200],
  },
  {
    type: "updates",
    label: "Updates",
    dotColor: brand[500],
    textColor: brand[600],
    borderColor: brand[500],
    countTextColor: brand[600],
    countBgColor: brand[50],
    countBorderColor: brand[200],
  },
  {
    type: "archive",
    label: "Archive",
    dotColor: errorColors[500],
    textColor: errorColors[600],
    borderColor: errorColors[500],
    countTextColor: errorColors[700],
    countBgColor: errorColors[50],
    countBorderColor: errorColors[200],
  },
  {
    type: "reactivate",
    label: "Reactivate",
    dotColor: warningColors[500],
    textColor: warningColors[600],
    borderColor: warningColors[500],
    countTextColor: warningColors[700],
    countBgColor: warningColors[50],
    countBorderColor: warningColors[200],
  },
];

interface PreviewState {
  open: boolean;
  loading: boolean;
  name: string;
  email: string;
  attributes: PreviewAttribute[];
  showDiff: boolean;
}

const DEFAULT_SORT: { orderBy: string; order: "asc" | "desc" } = {
  orderBy: "firstName",
  order: "asc",
};

const EMPTY_PREVIEW: PreviewState = {
  open: false,
  loading: false,
  name: "",
  email: "",
  attributes: [],
  showDiff: false,
};

export const ReviewChanges: React.FC<ReviewChangesProps> = ({
  jobId,
  includedSections,
  toggleSection,
  execute,
  isExecuting,
  goToStep,
}) => {
  const { portalUid } = useParams<{ portalUid: string }>();
  const { showToast } = useToast();

  const [selectedSection, setSelectedSection] = useState<SectionType>("new");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    orderBy: string;
    order: "asc" | "desc";
  }>(DEFAULT_SORT);

  const [rows, setRows] = useState<UserImportJobReviewUserRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState<UserImportJobReviewSummary | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<PreviewState>(EMPTY_PREVIEW);

  useEffect(() => {
    if (!portalUid || !jobId) return;
    let cancelled = false;
    setIsLoading(true);
    getUserImportJobReview(portalUid, jobId, {
      actionType: selectedSection as unknown as UserImportJobRecordActionType,
      search: search || undefined,
      limit: rowsPerPage,
      offset: (page - 1) * rowsPerPage,
      orderBy: sortConfig?.orderBy,
      sort: sortConfig?.order,
    })
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows);
        setTotalCount(res.count);
        setSummary(res.summary);
      })
      .catch((err) => {
        if (!cancelled)
          showToast(
            extractErrorMessage(err, "Failed to load review data"),
            "error",
          );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    portalUid,
    jobId,
    selectedSection,
    search,
    page,
    rowsPerPage,
    sortConfig,
    showToast,
  ]);

  const selectSection = (section: SectionType) => {
    setSelectedSection(section);
    setPage(1);
    setSearch("");
    setSortConfig(DEFAULT_SORT);
  };

  const sectionCount = (section: SectionType): number => {
    if (!summary) return 0;
    return section === "archive" ? summary.archived : summary[section];
  };

  const handleViewDetails = async (row: UserImportJobReviewUserRow) => {
    if (!portalUid || !jobId) return;
    const showDiff = selectedSection !== "new";
    setPreview({
      open: true,
      loading: true,
      name: `${row.firstName} ${row.lastName}`.trim(),
      email: row.email,
      attributes: [],
      showDiff,
    });
    try {
      const res = await getUserImportJobDetail(
        portalUid,
        jobId,
        row.UserImportJobRecordUid,
      );
      const record = res.records;
      const attributes: PreviewAttribute[] = record.attributes.map((a) => ({
        attributeKey: a.attributeKey,
        attributeCurrentValue:
          "attributeCurrentValue" in a ? a.attributeCurrentValue : undefined,
        attributeNewValue: a.attributeNewValue,
      }));
      setPreview((prev) => ({
        ...prev,
        loading: false,
        name: record.name || prev.name,
        email: record.email || prev.email,
        attributes,
      }));
    } catch (err) {
      showToast(extractErrorMessage(err, "Failed to load details"), "error");
      setPreview(EMPTY_PREVIEW);
    }
  };

  const columns: ColumnDef<UserImportJobReviewUserRow>[] = useMemo(() => {
    const base: ColumnDef<UserImportJobReviewUserRow>[] = [
      {
        id: "firstName",
        label: "First name",
        sortable: true,
        getValue: (r) => r.firstName,
      },
      {
        id: "lastName",
        label: "Last name",
        sortable: true,
        getValue: (r) => r.lastName,
      },
      {
        id: "email",
        label: "Email",
        sortable: true,
        getValue: (r) => r.email,
      },
    ];
    if (selectedSection !== "archive") {
      base.push({
        id: "viewDetails",
        label: "",
        align: "right",
        render: (row) => (
          <Button
            variant="text"
            onClick={() => handleViewDetails(row)}
            sx={{
              color: brand[600],
              fontWeight: 600,
              minWidth: 0,
              p: 0,
              "&:hover": {
                background: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            View Details
          </Button>
        ),
      });
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection, portalUid, jobId]);

  const noSectionsSelected = !Object.values(includedSections).some(Boolean);

  return (
    <>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Review &amp; Preview Changes
          </Typography>
          <Stack direction="row" spacing={2}>
            {SECTIONS.map((section) => {
              const isSelected = selectedSection === section.type;
              const isIncluded = includedSections[section.type];
              return (
                <Box
                  key={section.type}
                  onClick={() => selectSection(section.type)}
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: "12px",
                    border: `1px solid ${
                      isSelected ? section.borderColor : gray[200]
                    }`,
                    bgcolor: "background.paper",
                    cursor: "pointer",
                    transition: "border-color 0.15s, background-color 0.15s",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: section.dotColor,
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color={section.textColor}
                      >
                        {section.label}
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        px: 1,
                        height: 26,
                        minWidth: 26,
                        borderRadius: "999px",
                        bgcolor: section.countBgColor,
                        color: section.countTextColor,
                        border: `1px solid ${section.countBorderColor}`,
                        fontSize: 12,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {sectionCount(section.type)}
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Include in execution
                    </Typography>
                    <Switch
                      checked={isIncluded}
                      onChange={(e) =>
                        toggleSection(section.type, e.target.checked)
                      }
                      onClick={(e) => e.stopPropagation()}
                      color="success"
                    />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>

        <DataTable<UserImportJobReviewUserRow>
          data={rows}
          columns={columns}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptionsOverride={[5, 10, 25]}
          onPageChange={setPage}
          onRowsPerPageChange={(rows) => {
            setRowsPerPage(rows);
            setPage(1);
          }}
          sort={sortConfig}
          alwaysShowSortArrows
          firstClickOrder="desc"
          onSortChange={(orderBy, order) => {
            setSortConfig({ orderBy, order });
            setPage(1);
          }}
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          searchPlaceholder="Search by name or email"
          getRowId={(row) => row.UserImportJobRecordUid}
          isLoading={isLoading}
        />

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            onClick={() => goToStep(1)}
            startIcon={
              <ArrowBackIosNewIcon sx={{ fontSize: "13px !important" }} />
            }
            sx={{
              borderRadius: "12px",
              px: 3,
              borderColor: gray[300],
              color: "text.primary",
              fontWeight: 600,
              "&:hover": { borderColor: gray[400], bgcolor: gray[50] },
            }}
          >
            Back
          </Button>
          <Button
            onClick={execute}
            disabled={noSectionsSelected || isExecuting || !jobId}
            endIcon={
              isExecuting ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : undefined
            }
            sx={{
              borderRadius: "12px",
              px: 3,
              bgcolor: brand[600],
              color: "white",
              "&:hover": { bgcolor: brand[700] },
              "&.Mui-disabled": { bgcolor: gray[200], color: gray[400] },
            }}
          >
            {isExecuting ? "Executing…" : "Confirm & Execute"}
          </Button>
        </Stack>
      </Stack>

      <UserPreviewModal
        open={preview.open}
        loading={preview.loading}
        name={preview.name}
        email={preview.email}
        attributes={preview.attributes}
        showDiff={preview.showDiff}
        onClose={() => setPreview(EMPTY_PREVIEW)}
      />
    </>
  );
};
